import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Image } from 'react-bootstrap';
import '../../styles/login.css';
import { useThemeMode } from '../../ThemeContext';
import { authAPI } from '../../helpers/api_service';
import { MessageAlert } from '../Common';
import PasswordIcon from '../../assets/password.svg';
import EyeOpenIcon from '../../assets/eye-open.svg';
import LoginBanner1 from '../../assets/loginban1.png';
import LoginBanner2 from '../../assets/loginban2.png';
import LoginBanner3 from '../../assets/loginban3.png';
import Logo from '../../assets/logo.svg';
import ChevronRight from '../../assets/chevron-right.svg';

const loginBanners = [LoginBanner1, LoginBanner2, LoginBanner3];

function Resetpassword() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { mode } = useThemeMode();
  const location = useLocation();
  const navigate = useNavigate();
  const resetToken = location.state?.reset_token || '';

  // Image slider effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % loginBanners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.password || !formData.confirmPassword) {
      setError('Please fill in both fields.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!resetToken) {
      setError('Missing reset token.');
      return;
    }
    setIsLoading(true);
    try {
      const data = await authAPI.resetPassword({ new_password: formData.password, reset_token: resetToken });
      if (data.status === 'success') {
        navigate('/login', { state: { passwordResetSuccess: true } });
      } else {
        setError(data.message || 'Failed to reset password.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`login-body ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
      <Container fluid className="login-container">
        <Row className="g-0 min-vh-100">
          {/* Left Side: Image/Overlay with Logo */}
          <Col lg={6} className="d-none d-lg-block login-left-bg position-relative">
            <div className="login-slider-img-wrap position-absolute w-100 h-100">
              <Image 
                src={loginBanners[currentBanner]} 
                alt="Login Banner" 
                className="login-slider-img" 
                style={{ filter: 'brightness(1.25)' }}
                fluid
              />
            </div>
            <div className="login-left-overlay"></div>
            <div style={{ position: 'absolute', top: 40, left: 40, display: 'flex', alignItems: 'center', gap: 16 }}>
              <Image src={Logo} alt="Complimate Logo" style={{ height: 48 }} />
              <span style={{ color: '#fff', fontSize: 28, fontWeight: 600, letterSpacing: 1 }}>Complimate</span>
            </div>
          </Col>
          {/* Right Side: Reset Password Form */}
          <Col xs={12} lg={6} className="d-flex align-items-center justify-content-center login-right-bg position-relative">
            <div className="login-blur-bg-top"></div>
            <div className="login-blur-bg-bottom"></div>
            {/* Previous Button */}
            <div style={{ position: 'absolute', top: 32, left: 32, zIndex: 2 }}>
              <span style={{ color: mode === 'dark' ? '#bdbdbd' : '#222', fontWeight: 500, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => navigate(-1)}>
                <span style={{ fontSize: 20, fontWeight: 700, marginRight: 2 }}>&lt;</span> Previous
              </span>
            </div>
            <div className="login-form-wrapper w-100" style={{ maxWidth: '486px' }}>
              {/* Logo for mobile / small screens */}
              <div className="text-center mb-4 d-lg-none" style={{ marginTop: '24px' }}>
                <Image
                  src={Logo}
                  alt="Complimate Logo"
                  className="login-logo-main"
                  style={{ height: '64px' }}
                />
              </div>
              <div className="mb-4 text-center" style={{ marginTop: '32px' }}>
                <h2 className="login-title" style={{ color: mode === 'dark' ? '#26c3c0' : '#009391' }}>Set a New Password</h2>
                <div className="login-subtitle">Create a strong new password for your account. Make sure it’s something only you can remember.</div>
              </div>
              <MessageAlert 
                type="error" 
                show={!!error}
                message={error}
              />
              <Form onSubmit={handleSubmit} autoComplete="off">
                <div className="mb-3 position-relative">
                  <span className="login-icon password-icon">
                    <Image src={PasswordIcon} alt="Password Icon" width={20} height={20} />
                  </span>
                  <Form.Control 
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="login-input ps-5"
                    placeholder="Enter New Password"
                  />
                  <span 
                    className="login-icon eye-icon" 
                    onClick={() => setShowPassword(v => !v)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Image src={EyeOpenIcon} alt="Show Password" width={20} height={20} />
                  </span>
                </div>
                <div className="mb-4 position-relative">
                  <span className="login-icon password-icon">
                    <Image src={PasswordIcon} alt="Password Icon" width={20} height={20} />
                  </span>
                  <Form.Control 
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="login-input ps-5"
                    placeholder="Confirm New Password"
                  />
                  <span 
                    className="login-icon eye-icon" 
                    onClick={() => setShowConfirmPassword(v => !v)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Image src={EyeOpenIcon} alt="Show Password" width={20} height={20} />
                  </span>
                </div>
                <Button 
                  type="submit" 
                  className="btn-teal w-100 login-btn d-flex align-items-center justify-content-center mb-4"
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                  <span className="ms-2">
                    <Image src={ChevronRight} alt="chevron" style={{ height: '14px' }} />
                  </span>
                </Button>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Resetpassword; 